/**
 * U-CTX-01: the selected ship and where it can sail stay in sight. When a ship is selected (or
 * its reach appears), the camera moves the least it takes for them to lie in the part of the
 * board the inspector and the top bar leave open — wherever the card stands: at the right on
 * desktops, at the bottom on phones, at the left on landscape phones.
 */
import { useEffect } from 'react';
import type { Camera } from '../board/camera.ts';
import { keepInside, type ScreenArea } from '../board/focus.ts';
import type { ProjectionKind } from '../board/projection.ts';

/** Room kept round the card and under the top bar, CSS px. */
const GAP = 12;
const TOP_BAR = 64;

/**
 * The board's part no card covers: of the four ways to keep clear of the card (left, right,
 * above or below it), the one that leaves the most room. A tall card at the right leaves the
 * left of the board; the same card folded to a strip at the top leaves everything below it.
 */
export function openArea(board: DOMRect, card: DOMRect | null): ScreenArea {
  const area = { left: GAP, top: TOP_BAR, right: board.width - GAP, bottom: board.height - GAP };
  if (!card) return area;
  const c = {
    left: card.left - board.left - GAP,
    top: card.top - board.top - GAP,
    right: card.right - board.left + GAP,
    bottom: card.bottom - board.top + GAP,
  };
  const options: ScreenArea[] = [
    { ...area, right: Math.min(area.right, c.left) },
    { ...area, left: Math.max(area.left, c.right) },
    { ...area, bottom: Math.min(area.bottom, c.top) },
    { ...area, top: Math.max(area.top, c.bottom) },
  ];
  const size = (a: ScreenArea) => Math.max(0, a.right - a.left) * Math.max(0, a.bottom - a.top);
  return options.reduce((best, a) => (size(a) > size(best) ? a : best));
}

/**
 * Keeps `cells` (the selected unit's own cell first, then its reach) clear of the inspector,
 * each time `key` changes: a new selection, its reach showing or going, the card opening.
 */
export function useKeepClear(
  camera: Camera,
  projection: ProjectionKind,
  key: string | null,
  cells: () => readonly string[],
): void {
  useEffect(() => {
    if (!key) return;
    const board = document.querySelector('[data-testid="board"]')?.getBoundingClientRect();
    if (!board) return;
    const card = document.querySelector('[data-testid="inspector"]')?.getBoundingClientRect() ?? null;
    keepInside(camera, projection, cells(), openArea(board, card));
    // Only a new selection or reach moves the camera, not every change of the game.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}
